import { createEffect, untrack } from "solid-js"
import { Group, Vector3, type Object3D } from "three"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import applyEntityWalkPathToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityWalkPathToNextObject3DFrame"
import { MediumMissileAntiAirEntity } from "../../core/entity/EntityType/MediumMissileAntiAir"
import shouldEntityShowFadedFrames from "../../core/entity/shouldEntityShowFadedFrames"
import tmpRng from "../../core/rng/tmpRng"
import { PI } from "../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import assertClassInDev from "../../ldsh/assertClassInDev"
import { CrotaleADMS1_CrotaleADMS1_GLTF_Model } from "../gltf_models.generated"
import { appGltfLoader } from "./appGltfLoader"
import applyPlayerColors from "./applyPlayerColors"
import creaseGeometryNormalsByMeshNameList from "./creaseGeometryNormalsByMeshNameList"
import cubicEaseInOutBounce from "./cubicEaseInOutBounce"
import makeLightReconWheelScene from "./makeLightReconWheelScene"
import runWithObject3DOwner from "./runWithObject3DOwner"

const totalLoopMs = 1000

// const heights = [1, 2, 2, 3, 3, 4]

const meshesToCrease = [
  // 'Body_1',
  // 'Body_2',
  // 'Body_3',
  'Turret_1', // the dish thing
  // 'Turret_2',
  // 'Turret_3',
  'Barrel_1',
  // 'Barrel_2', // actually the rack
]

export default function makeMediumMissileAntiAirScene (
  engine: Engine,
  ent: EntityGraphNode<MediumMissileAntiAirEntity>,
  callback: (newGroup: Object3D) => void
): void {
  return appGltfLoader.load(CrotaleADMS1_CrotaleADMS1_GLTF_Model, async function (ModelObj) {
    tmpRng.seed = ent.id
    const { scene } = ModelObj
    // printObjectHierarchy(scene)

    const bodyMesh = scene.getObjectByName('Body') as Group
    assertClassInDev(bodyMesh, Group)

    const turretGroup = scene.getObjectByName('Turret') as Group
    assertClassInDev(turretGroup, Group)

    const barrelGroup = scene.getObjectByName('Barrel') as Group
    assertClassInDev(barrelGroup, Group)

    // bodyMesh.position.y += 0.5

    barrelGroup.rotateX(-PI / 7)

    const wheelX = 3.4
    const wheelZ = 0.8
    const wheelVectorsList : Array<Vector3> = [
      new Vector3(wheelX, wheelZ, -6.5),
      new Vector3(wheelX, wheelZ, -1.05),
      new Vector3(wheelX, wheelZ, 4.5),
      new Vector3(-wheelX, wheelZ, -6.5),
      new Vector3(-wheelX, wheelZ, -1.05),
      new Vector3(-wheelX, wheelZ, 4.5),
    ]

    const wheelScaleX = 0.6

    await new Promise<void> (resolve => makeLightReconWheelScene(engine, ent, (wheelSceneBase) => {
      wheelVectorsList.forEach((v) => {
        const isLeftWheel = v.x < 0
        const wheelScene = wheelSceneBase.clone()
        wheelScene.scale.multiplyScalar(wheelScaleX)

        if (isLeftWheel) {
          wheelScene.rotation.y = PI
        }

        wheelScene.position.copy(v)
        // wheelScene.position = v
        // const wheelPosition = wheelScene.position

        // wheelPosition.set(v.x, 0, v.z)

        scene.add(wheelScene)
      })
      resolve()
    }))

    applyPlayerColors(engine, scene, ent)

    const defaultHullY = bodyMesh.position.y
    // const defaultHullX = 0

    let entValue = untrack(ent.raw)
    runWithObject3DOwner(scene, () => {
      createEffect(() => {
        entValue = ent.raw()
      })
    })

    mountOnFrameUpdateToObject3D(engine, bodyMesh, (elapsedMs: number, frameAt: number): void => {
      const mod = frameAt % totalLoopMs
      if (shouldEntityShowFadedFrames(entValue)) {
        bodyMesh.position.y = defaultHullY
      } else if (entValue.player_id) {
        bodyMesh.position.y = defaultHullY + (cubicEaseInOutBounce(mod / 500) / 7)
      }

      // barrelMesh.rotation.x = Math.random() > 0.5 ? landToAirttackBarrelRotationX
      //   : landToLandAttackBarrelRotationX
      applyEntityRotationToNextObject3DFrame(scene, elapsedMs)
      applyEntityWalkPathToNextObject3DFrame(scene, elapsedMs)
    })

    creaseGeometryNormalsByMeshNameList(scene, meshesToCrease)

    callback(scene)
  })
}

